import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function money(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function safeFileName(value) {
  return String(value || "")
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function splitBreakdown(lines = []) {
  const earnings = [];
  const deductions = [];
  const employerContributions = [];

  lines.forEach((line) => {
    if (line.component_type === "earning") earnings.push(line);
    else if (line.component_type === "deduction") deductions.push(line);
    else if (line.component_type === "employer_contribution") {
      employerContributions.push(line);
    }
  });

  return { earnings, deductions, employerContributions };
}

function getPayeLine(lines = []) {
  return (
    lines.find((line) => {
      const code = String(line.component_code || "").toUpperCase();
      const name = String(line.component_name || "").toLowerCase();

      return (
        code === "PAYE" ||
        code.includes("PAYE") ||
        name.includes("paye") ||
        name.includes("pay as you earn") ||
        (name.includes("tax") && !name.includes("tax relief"))
      );
    }) || null
  );
}

function getRentReliefLine(lines = []) {
  return (
    lines.find(
      (line) =>
        String(line.component_code || "").toUpperCase() === "RENT_RELIEF" ||
        String(line.component_name || "")
          .toLowerCase()
          .includes("rent relief")
    ) || null
  );
}

function ensureSpace(doc, currentY, neededHeight = 24) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + neededHeight > pageHeight - 20) {
    doc.addPage();
    return 20;
  }
  return currentY;
}

function buildPayeComputation(payeLine, deductions = []) {
  const metadata = payeLine?.metadata || {};

  const monthlyGrossIncome = Number(metadata.monthly_gross_income || 0);
  const payComponentDeductions = Number(metadata.pay_component_deductions || 0);
  const annualRentRelief = Number(metadata.annual_rent_relief || 0);
  const monthlyRentRelief = Number(metadata.monthly_rent_relief || 0);
  const monthlyTaxableIncome = Number(metadata.monthly_taxable_income || 0);
  const annualTaxableIncome = Number(metadata.annual_taxable_income || 0);
  const annualPayePayable = Number(metadata.annual_paye_payable || 0);
  const monthlyPayePayable =
    Number(metadata.monthly_paye_payable || 0) || Number(payeLine?.amount || 0);

  const nonCashDeductions = deductions.filter(
    (item) => item?.metadata?.non_cash === true
  );

  return {
    monthlyGrossIncome,
    payComponentDeductions,
    annualRentRelief,
    monthlyRentRelief,
    monthlyTaxableIncome,
    annualTaxableIncome,
    annualPayePayable,
    monthlyPayePayable,
    nonCashDeductions,
    taxBreakdown: Array.isArray(metadata.tax_breakdown)
      ? metadata.tax_breakdown
      : [],
  };
}

function buildPayslipDoc({ company, employee, run, breakdown = [] }) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const brandTeal = [0, 128, 128];
  const brandGold = [237, 179, 45];
  const dark = [51, 51, 51];
  const muted = [95, 111, 111];
  const lightBg = [245, 250, 250];

  const employeeName =
    employee?.employee_name ||
    `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() ||
    "Employee";

  const runName = run?.run_name || "Payroll Run";
  const payDate =
    run?.approved_at?.slice(0, 10) ||
    run?.processed_at?.slice(0, 10) ||
    run?.created_at?.slice(0, 10) ||
    "-";

  const grossPay = Number(employee?.gross_pay || 0);
  const totalDeductions = Number(employee?.total_deductions || 0);
  const employerContributionValue = Number(
    employee?.employer_contributions || 0
  );
  const netPay = Number(employee?.net_pay || 0);

  const { earnings, deductions, employerContributions } =
    splitBreakdown(breakdown);

  doc.setFillColor(...brandTeal);
  doc.rect(0, 0, pageWidth, 34, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.text(company?.company_name || "KostPadi Company", 14, 16);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Employee Payslip", 14, 24);

  doc.setFillColor(...brandGold);
  doc.rect(14, 38, pageWidth - 28, 4, "F");

  doc.setFillColor(...lightBg);
  doc.roundedRect(14, 48, 88, 52, 4, 4, "F");
  doc.roundedRect(108, 48, 88, 52, 4, 4, "F");

  doc.setTextColor(...muted);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("EMPLOYEE DETAILS", 18, 56);
  doc.text("PAYROLL / BANK DETAILS", 112, 56);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...dark);
  doc.setFontSize(9);

  doc.text(`Name: ${employeeName}`, 18, 63);
  doc.text(`Employee Code: ${employee?.employee_code || "-"}`, 18, 69);
  doc.text(`Department: ${employee?.department || "-"}`, 18, 75);
  doc.text(`Job Title: ${employee?.job_title || "-"}`, 18, 81);
  doc.text(`Level: ${employee?.job_level_name || "-"}`, 18, 87);
  doc.text(`Grade: ${employee?.job_grade_name || "-"}`, 18, 93);

  doc.text(`Payroll Run: ${runName}`, 112, 63);
  doc.text(`Pay Date: ${payDate}`, 112, 69);
  doc.text(`Salary Basis: ${employee?.salary_basis || "-"}`, 112, 75);
  doc.text(`Bank: ${employee?.bank_name || "-"}`, 112, 81);
  doc.text(`Acct Name: ${employee?.bank_account_name || "-"}`, 112, 87);
  doc.text(`Acct No: ${employee?.bank_account_number || "-"}`, 112, 93);

  doc.setFillColor(252, 255, 255);
  doc.roundedRect(14, 108, pageWidth - 28, 22, 4, 4, "F");
  doc.setDrawColor(216, 236, 236);
  doc.roundedRect(14, 108, pageWidth - 28, 22, 4, 4, "S");

  const summaryY = 116;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...muted);
  doc.text("GROSS PAY", 18, summaryY);
  doc.text("DEDUCTIONS", 72, summaryY);
  doc.text("NET PAY", 126, summaryY);
  doc.text("EMPLOYER CONTR.", 168, summaryY);

  doc.setTextColor(...dark);
  doc.setFontSize(11);
  doc.text(money(grossPay), 18, summaryY + 8);
  doc.text(money(totalDeductions), 72, summaryY + 8);
  doc.text(money(netPay), 126, summaryY + 8);
  doc.text(money(employerContributionValue), 168, summaryY + 8);

  const earningsRows = earnings.map((item) => [
    item.component_name || "-",
    money(item.amount || 0),
  ]);

  const deductionRows = deductions.map((item) => {
    const code = String(item.component_code || "").toUpperCase();
    const isRentRelief =
      code === "RENT_RELIEF" ||
      String(item.component_name || "")
        .toLowerCase()
        .includes("rent relief");

    return [
      isRentRelief
        ? `${item.component_name || "Rent Relief"} (Tax Relief - Non-cash)`
        : item?.metadata?.non_cash
        ? `${item.component_name || "-"} (Non-cash)`
        : item.component_name || "-",
      money(item.amount || 0),
    ];
  });

  if (!earningsRows.length) earningsRows.push(["No earnings lines", "0.00"]);
  if (!deductionRows.length) deductionRows.push(["No deduction lines", "0.00"]);

  autoTable(doc, {
    startY: 138,
    margin: { left: 14, right: 108 },
    head: [["Pay Components / Earnings / Allowances", "Amount"]],
    body: earningsRows,
    theme: "grid",
    styles: {
      fontSize: 8.8,
      textColor: dark,
      cellPadding: 3,
      lineColor: [230, 240, 240],
    },
    headStyles: {
      fillColor: brandTeal,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    foot: [["Total Earnings", money(grossPay)]],
    footStyles: {
      fillColor: [240, 249, 249],
      textColor: dark,
      fontStyle: "bold",
    },
  });

  const earningsTableEndY = doc.lastAutoTable?.finalY || 138;

  autoTable(doc, {
    startY: 138,
    margin: { left: 108, right: 14 },
    head: [["Deductions / Statutory / Loans / Benefits", "Amount"]],
    body: deductionRows,
    theme: "grid",
    styles: {
      fontSize: 8.8,
      textColor: dark,
      cellPadding: 3,
      lineColor: [230, 240, 240],
    },
    headStyles: {
      fillColor: brandGold,
      textColor: dark,
      fontStyle: "bold",
    },
    foot: [["Total Deductions", money(totalDeductions)]],
    footStyles: {
      fillColor: [255, 248, 235],
      textColor: dark,
      fontStyle: "bold",
    },
  });

  const deductionTableEndY = doc.lastAutoTable?.finalY || 138;
  let currentY = Math.max(earningsTableEndY, deductionTableEndY) + 10;

  if (employerContributions.length) {
    currentY = ensureSpace(doc, currentY, 36);

    const employerRows = employerContributions.map((item) => [
      item.component_name || "-",
      money(item.amount || 0),
    ]);

    autoTable(doc, {
      startY: currentY,
      margin: { left: 14, right: 14 },
      head: [["Employer Contributions", "Amount"]],
      body: employerRows,
      theme: "grid",
      styles: {
        fontSize: 8.8,
        textColor: dark,
        cellPadding: 3,
        lineColor: [230, 240, 240],
      },
      headStyles: {
        fillColor: [51, 51, 51],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      foot: [
        ["Total Employer Contributions", money(employerContributionValue)],
      ],
      footStyles: {
        fillColor: [249, 250, 251],
        textColor: dark,
        fontStyle: "bold",
      },
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  const payeLine = getPayeLine(breakdown);
  const rentReliefLine = getRentReliefLine(breakdown);
  const payeComputation = buildPayeComputation(payeLine, deductions);

  if (payeLine?.metadata) {
    currentY = ensureSpace(doc, currentY, 50);

    autoTable(doc, {
      startY: currentY,
      margin: { left: 14, right: 14 },
      head: [["PAYE Computation Item", "Amount"]],
      body: [
        ["Monthly Gross Income", money(payeComputation.monthlyGrossIncome)],
        [
          "Less: Pay Component Deductions",
          money(payeComputation.payComponentDeductions),
        ],
        ["Less: Monthly Rent Relief", money(payeComputation.monthlyRentRelief)],
        ["Monthly Taxable Income", money(payeComputation.monthlyTaxableIncome)],
        [
          "Annual Taxable Income (Monthly Taxable × 12)",
          money(payeComputation.annualTaxableIncome),
        ],
        ["Annual PAYE Payable", money(payeComputation.annualPayePayable)],
        ["Monthly PAYE Payable", money(payeComputation.monthlyPayePayable)],
      ],
      theme: "grid",
      styles: {
        fontSize: 8.5,
        textColor: dark,
        cellPadding: 3,
        lineColor: [230, 240, 240],
      },
      headStyles: {
        fillColor: brandTeal,
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      foot: [
        [
          "PAYE Formula",
          "Monthly Gross - Monthly Rent Relief - Pay Component Deductions; then × 12; then progressive tax; then ÷ 12",
        ],
      ],
      footStyles: {
        fillColor: [240, 249, 249],
        textColor: dark,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 95 },
        1: { cellWidth: "auto" },
      },
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  if (payeComputation.taxBreakdown.length) {
    currentY = ensureSpace(doc, currentY, 42);

    const payeRows = payeComputation.taxBreakdown.map((band) => [
      band.label || "-",
      money(band.rate || 0),
      money(band.taxable_amount || 0),
      money(band.tax_amount || 0),
    ]);

    autoTable(doc, {
      startY: currentY,
      margin: { left: 14, right: 14 },
      head: [["PAYE Band", "Rate %", "Taxable Amount", "Tax Amount"]],
      body: payeRows,
      theme: "grid",
      styles: {
        fontSize: 8.5,
        textColor: dark,
        cellPadding: 3,
        lineColor: [230, 240, 240],
      },
      headStyles: {
        fillColor: [51, 51, 51],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      foot: [
        ["Annual PAYE Total", "", "", money(payeComputation.annualPayePayable)],
        [
          "Monthly PAYE Charged",
          "",
          "",
          money(payeComputation.monthlyPayePayable),
        ],
      ],
      footStyles: {
        fillColor: [249, 250, 251],
        textColor: dark,
        fontStyle: "bold",
      },
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  if (rentReliefLine) {
    currentY = ensureSpace(doc, currentY, 28);

    autoTable(doc, {
      startY: currentY,
      margin: { left: 14, right: 14 },
      head: [["Tax Relief Item", "Amount", "Note"]],
      body: [
        [
          rentReliefLine.component_name || "Rent Relief",
          money(
            Number(
              rentReliefLine?.metadata?.monthly_rent_relief ||
                payeComputation.monthlyRentRelief ||
                rentReliefLine.amount ||
                0
            )
          ),
          "Used only to reduce taxable income. It is non-cash and does not directly reduce net pay.",
        ],
      ],
      theme: "grid",
      styles: {
        fontSize: 8.5,
        textColor: dark,
        cellPadding: 3,
        lineColor: [230, 240, 240],
      },
      headStyles: {
        fillColor: brandGold,
        textColor: dark,
        fontStyle: "bold",
      },
    });

    currentY = doc.lastAutoTable.finalY + 10;
  }

  currentY = ensureSpace(doc, currentY, 24);

  doc.setFillColor(...brandTeal);
  doc.roundedRect(14, currentY, pageWidth - 28, 18, 4, 4, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`NET PAY: ${money(netPay)}`, 18, currentY + 11);

  const footerY = pageHeight - 16;
  doc.setTextColor(...muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(company?.address || "Generated from KostPadi Payroll", 14, footerY);
  doc.text(
    "This is a system-generated payslip from KostPadi Payroll.",
    14,
    pageHeight - 10
  );

  return doc;
}

export function generatePayslipPDF({ company, employee, run, breakdown = [] }) {
  const doc = buildPayslipDoc({ company, employee, run, breakdown });

  const employeeName =
    employee?.employee_name ||
    `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() ||
    "Employee";

  const runName = run?.run_name || "Payroll_Run";

  doc.save(
    `Payslip_${safeFileName(employeeName)}_${safeFileName(runName)}.pdf`
  );
}

export function generatePayslipBlob({
  company,
  employee,
  run,
  breakdown = [],
}) {
  const doc = buildPayslipDoc({ company, employee, run, breakdown });

  const employeeName =
    employee?.employee_name ||
    `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim() ||
    "Employee";

  const runName = run?.run_name || "Payroll_Run";

  return {
    fileName: `Payslip_${safeFileName(employeeName)}_${safeFileName(
      runName
    )}.pdf`,
    blob: doc.output("blob"),
  };
}
